import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import Icon, { IconType } from '../Icon/Icon';
import { toast } from 'react-toastify';
import { UiValuePairs } from '../../utils/CommonTypes/UserTypes';
import Modal from '../Modal/Modal';
import { parse } from 'query-string';
import StoreX, { store } from '../../redux/oldStore';
import { Server } from '../../utils/Server';
import { ExpandableImg } from './EasyImage';

export { EasyImage, ExpandableImg, ExpandableVideo, SlickImage, SlickImageGroup } from './EasyImage';
export type { IImage, IVideo, IExpandableImgProps, IExpandableVideoProps } from './EasyImage';

const PdfViewer = lazy(() => import('../PdfViewer/PdfViewer'));

interface IExpandableDivProps {
  children?: React.ReactNode;
}

const ExpandableDiv = (props: IExpandableDivProps) => {
  const [open, setOpen] = useState<boolean>(false);

  return (
    <div className={`expandable-div ${open ? 'open' : 'closed'}`}>
      {props.children}
      <div className="bottom-gradient btn-holder">
        {open ? (
          <button
            type="button"
            onClick={() => {
              setOpen(false);
            }}
            className="more-button btn btn-default btn-xs">
            <Icon type={IconType.up} /> See less
          </button>
        ) : (
          <button
            type="button"
            onClick={() => {
              setOpen(true);
            }}
            className="more-button btn btn-default btn-xs">
            <Icon type={IconType.down} /> See more
          </button>
        )}
      </div>
    </div>
  );
};

export default ExpandableDiv;
interface IViewrContainerProps {
  children?: React.ReactNode;
}

export const ViewerContainer = (props: IViewrContainerProps) => {};

interface ILinkToView {
  type: 'pdf' | 'video' | 'custom-forms';
  urlSource: string;
  name?: string;
  fill?: boolean;
  thumbnail?: string;
}

interface ICustomFormsData {
  Url: string;
  Values: any;
  OverlayValues: boolean;
}

export const LinkToView = (props: ILinkToView) => {
  const [open, setOpen] = useState<boolean>(false);
  const [url, setUrl] = useState<string>('');
  const [customFormsData, setCustomFormsData] = useState<ICustomFormsData>();

  useEffect(() => {
    let u = props.urlSource;
    if (u.startsWith('https://localhost')) u = u.replace('https://', 'http://');
    setUrl(u);
  }, []);

  const LoadIfNeeded = async () => {
    if (props.type === 'custom-forms' && !customFormsData) {
      store.server.getApi2<ICustomFormsData>(`/GenericForms/Response/${props.urlSource}`).then((res) => {
        if (res && res.Value) {
          if (res.Success) {
            setCustomFormsData(res.Value);
          } else {
            toast.error(`Error loading form response: ${res.Message}`);
          }
        }
      });
    }
  };

  const renderIt = () => {
    switch (props.type) {
      case 'video':
        return renderVideoClosed();
        break;
      case 'custom-forms':
      case 'pdf':
        return renderPdfClosed();
        break;
    }
  };
  const renderItOpen = () => {
    switch (props.type) {
      case 'video':
        return renderVideo();
        break;
      case 'pdf':
        return renderPdf();
        break;
      case 'custom-forms':
        LoadIfNeeded();
        return renderCustomForms();
        break;
    }
  };

  const renderPdfClosed = () => {
    return (
      <span
        className={`link-to-show ${props.fill ? 'fill' : ''}`}
        title={`Click to view`}
        onClick={(e) => {
          setOpen(true);
        }}>
        <Icon type={IconType.filePdf} /> {props.name ?? props.urlSource}
        {props.thumbnail && (
          <img
            src={props.thumbnail}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        )}
      </span>
    );
  };

  const renderVideoClosed = () => {
    return (
      <span
        className={`link-to-show ${props.fill ? 'fill' : ''}`}
        title={`Click to view`}
        onClick={(e) => {
          setOpen(true);
        }}>
        <Icon type={IconType.film} /> {props.name ?? props.urlSource}
      </span>
    );
  };

  const viewer = (children: React.ReactNode) => {
    return (
      <div
        className="easy-viewer-container"
        onClick={(e) => {
          setOpen(false);
          e.stopPropagation();
        }}>
        <div
          className="easy-viewer"
          onClick={(e) => {
            e.stopPropagation();
          }}>
          <div className="viewer-header">
            <div className="viewer-title">{props.name ?? props.urlSource}</div>
            <div>
              <span
                className="click"
                title="click to close"
                onClick={() => {
                  setOpen(false);
                }}>
                <Icon type={IconType.close} />
              </span>
            </div>
          </div>
          <div className="viewer-body">{children}</div>
          {props.type !== 'custom-forms' && (
            <div className="viewer-footer">
              <hr />
              <a href={url} className="click" title="click to view file" target="_blank">
                {props.type === 'pdf' && <Icon type={IconType.filePdf} />}
                {/* {props.name ?? props.urlSource} */}
                Trouble Viewing? Open in new tab.
              </a>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPdf = () => {
    return (
      <>
        {viewer(
          <>
            <Suspense fallback={<div className="bumper text-center">Loading PDF...</div>}>
              <PdfViewer id={'x'} source={url} signRequested={() => {}} viewOnly={true} overlayValues={false} />
            </Suspense>
          </>,
        )}
      </>
    );
  };

  const renderCustomForms = () => {
    return (
      <>
        {customFormsData
          ? viewer(
              <>
                <Suspense fallback={<div className="bumper text-center">Loading PDF...</div>}>
                  <PdfViewer id={'x'} source={customFormsData.Url} values={customFormsData.Values} viewOnly={true} overlayValues={customFormsData.OverlayValues} />
                </Suspense>
              </>,
            )
          : viewer(
              <div className="bumper text-center ">
                <h3>Loading...</h3>
                The form is loading, please wait.
              </div>,
            )}
      </>
    );
  };

  const renderVideo = () => {
    return (
      <>
        {viewer(
          <>
            <video width="100%" height="100%" controls>
              <source src={url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </>,
        )}
      </>
    );
  };

  return (
    <>
      {!open && <>{renderIt()}</>}
      {open && <>{renderItOpen()}</>}
    </>
  );
};


interface IFileInfo {
  type: 'pdf' | 'image' | 'video' | 'text' | 'other';
  path: string;
  ext: string;
  urlSource?: string;
  fileName?: string;
}

interface IEasyFileInputProps {
  onSelect?: Function;
  onSelectFile?: Function;
  htmlId?: string;
  exts?: string;
  fileSize?: number;
  value?: string;
  valueOptions?: UiValuePairs[];
  filePath?: string;
  fileKey?: string;
  fileName?: string;
  readOnly?: boolean;
}
export const EasyFileInput = (props: IEasyFileInputProps) => {
  const [info, setFileInfo] = useState<IFileInfo>();
  const [showInput, setShowInput] = useState<boolean>(props?.value ? false : true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showOptions, setShowOptions] = useState<boolean>();
  const [loadingFailed, setLoadingFailed] = useState<boolean>(false);

  useEffect(() => {
    if (props.fileKey) {
      let fi = getFileInfo2(props.filePath ?? '', props.fileName ?? '');
      if (fi) setFileInfo(fi);
    } else {
      let fi = getFileInfo(props.value ?? '');
      if (fi && (!info?.urlSource || (info?.urlSource && fi?.path != info.path))) {
        setFileInfo(fi);
      }
    }
  }, [props.value, props.fileKey, props.fileName, props.filePath, props.fileKey]);

  const selectExistingFile = (x: UiValuePairs) => {
    let fi = getFileInfo(x.Value);
    if (props.onSelect) props.onSelect(x.Value);
    setFileInfo(fi ?? undefined);
    setShowOptions(false);
  };

  const onChangeFile = async (e) => {
    if (e.target.files) {
      let file = await isFileValid(e.target.files);

      if (!file) {
        if (e.target) e.target.files = [];
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      setFileInfo(file.fileInfo);
      setShowInput(false);
      setShowOptions(false);
      if (props.onSelectFile) props.onSelectFile(file.file);
    }
  };

  const isFileValid = async (files: FileList) => {
    if (!files) return;
    let file = files[0];

    let size = file.size / 1024.0 / 1024.0;
    let name = file.name;
    let fileInfo = getFileInfo(file.name);
    if (!fileInfo) return null;

    if (props.fileSize) {
      if (props.fileSize < size) {
        toast.warning(`The file "${name}" is too large. The max file size is ${props.fileSize}MB.`);
        return null;
      }
    } else {
      if (fileInfo?.type === 'video' && size > 1024) {
        //to big
        toast.warning(`The file "${name}" is too large. The max file size is 1 GB`);
        return null;
      } else if (fileInfo?.type === 'pdf' && size > 50) {
        //to big
        toast.warning(`The file "${name}" is too large. The max file size is 50 MB`);
        return null;
      } else if (size > 40) {
        //to big
        toast.warning(`The file "${name}" is too large. The max file size is 40 MB`);
        return null;
      }
    }

    fileInfo.urlSource = await readURL(file);

    return { file, fileInfo };
  };

  const getFileInfo = (filePath: string | undefined) => {
    if (!filePath || typeof filePath !== typeof ' ') return null;

    let ext = filePath.substring(filePath.lastIndexOf('.')).toLowerCase();
    let isPdf = ext === '.pdf';
    let isImage = ext === '.jpg' || ext === '.jpeg' || ext === '.png' || ext === '.tif' || ext === '.tiff' || ext === '.gif' || ext === '.svg' || ext === '.webp' || ext === '.heic';
    let isVideo = ['.mov', '.mp4'].find((x) => x === ext);
    let isText = ['.csv', '.xml', '.txt'].find((x) => x === ext);
    let type: FileType = isPdf ? 'pdf' : isImage ? 'image' : isVideo ? 'video' : isText ? 'text' : 'other';

    let result: IFileInfo = { type: type, path: filePath, ext: ext };
    return result;
  };
  const getFileInfo2 = (filePath: string, fileName: string) => {
    if (!filePath || typeof filePath !== typeof ' ') return null;

    let ext = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
    let isPdf = ext === '.pdf';
    let isImage = ext === '.jpg' || ext === '.jpeg' || ext === '.png' || ext === '.tif' || ext === '.tiff' || ext === '.gif' || ext === '.svg' || ext === '.webp' || ext === '.heic';
    let isVideo = ['.mov', '.mp4'].find((x) => x === ext);
    let isText = ['.csv', '.xml', '.txt'].find((x) => x === ext);
    let type: FileType = isPdf ? 'pdf' : isImage ? 'image' : isVideo ? 'video' : isText ? 'text' : 'other';

    let result: IFileInfo = { type: type, path: filePath, ext: ext, fileName: fileName };
    return result;
  };

  const readURL = (file) => {
    return new Promise<string>((res, rej) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target && e.target.result) {
          res(e.target.result as string);
        } else {
          rej(new Error('File reading error'));
        }
      };
      reader.onerror = (e) => rej(e);
      reader.readAsDataURL(file);
    });
  };

  const ParseFileName = (name: string) => {
    if (!name) return name;

    //last part of name after / or \
    let parts = name.split(/[/\\]/);
    let n = parts[parts.length - 1];

    //see if there are query strings with a name or filename
    if (n.indexOf('?') >= 0) {
      let qs = n.substring(n.indexOf('?') + 1);
      let dic = parse(qs);
      if (dic['name']) return dic['name'] as string;
      if (dic['fileName']) return dic['fileName'] as string;

      //remove query strings
      n = n.substring(0, n.indexOf('?'));
    }

    return n;
  };

  const showFile = () => {
    let namePath = info?.fileName ?? props.valueOptions?.find((x) => x.Value === info?.path)?.Name ?? info?.path ?? 'Unknown File Name';
    let name = ParseFileName(namePath);
    console.log('showing file', info, namePath, name);
    if (info?.type === 'image' || info?.type === 'pdf') {
      let url = info.urlSource ?? info.path;
      if (info.type === 'pdf') {
        //add thumbnail t=250
        url = url.indexOf('?') >= 0 ? `${url}&t=250` : `${url}?t=250`;
      }
      return (
        <div className="flex">
          {!loadingFailed && <img src={url} className="thumbnail-1" onError={() => setLoadingFailed(true)} />}
          {loadingFailed && (
            <div className="thumbnail-1">
              <Icon addClass="fa-4x" type={info?.type === 'pdf' ? IconType.filePdf : IconType.image} />
            </div>
          )}

          <div className="bumper">
            <div>
              <span
                className="click"
                onClick={() => {
                  setShowOptions(true);
                }}>
                <Icon type={IconType.edit} />
              </span>
              <span
                className="click"
                onClick={() => {
                  if (window.confirm('Are you sure you want to remove this selected file?')) {
                    setFileInfo(undefined);
                    if (props.onSelect) props.onSelect();
                  }
                }}>
                <Icon type={IconType.trashO} />
              </span>
            </div>
            <div>
              {name && (
                <div>
                  <strong>{name}</strong>
                </div>
              )}
              <div>
                <a href={info.urlSource ?? info.path} target="_blank" className="click" rel="noreferrer">
                  <Icon type={info.type === 'pdf' ? IconType.filePdf : IconType.image} /> View Full {info.type === 'pdf' ? 'PDF' : 'Image'}
                </a>
              </div>
            </div>
          </div>
        </div>
      );
    } else if (namePath && (namePath.toLowerCase().indexOf('http') === 0 || namePath.indexOf('../') === 0 || namePath.indexOf('/') === 0)) {
      let icon = IconType.file;
      if (info?.type === 'video') icon = IconType.film;
      else if (info?.type === 'text') icon = IconType.fileText;
      return (
        <div className="flex">
          <Icon type={icon} addClass="fa-5x" />
          <div className="bumper">
            <div>
              <span
                className="click"
                onClick={() => {
                  setShowOptions(true);
                }}>
                <Icon type={IconType.edit} />
              </span>
            </div>
            <div>
              <strong>{name}</strong>
            </div>
            <div>
              <a href={info.urlSource ?? info.path} target="_blank" className="click" rel="noreferrer">
                <Icon type={IconType.link} /> View File
              </a>
            </div>
          </div>
        </div>
      );
    } else {
      // if (info?.urlSource){
      return (
        <div className="flex">
          <strong>{name}</strong>
          <span
            className="click bumper"
            onClick={() => {
              setShowOptions(true);
            }}>
            <Icon type={IconType.edit} />
          </span>
        </div>
      );
    }
  };

  const nameOrFile = (x: UiValuePairs) => {
    let file = getFileInfo(x.Value);
    if (file && file.path) {
      switch (file.type) {
        case 'image':
          return (
            <div className="flex-between">
              <div className="thumbnail-1 click">
                <ExpandableImg
                  imageSrc={[{ alt: x.Name, path: file.path }]}
                  name={x.Name}
                  onClick={() => {
                    selectExistingFile(x);
                  }}
                />
              </div>
              <span className="bumper">{x.Name}</span>
            </div>
          );
        case 'pdf':
          return <LinkToView type="pdf" urlSource={file.path} name={x.Name} />;
        case 'video':
          return <LinkToView type="video" urlSource={file.path} name={x.Name} />;
        case 'text':
          break;
        case 'other':
          break;
      }
    }
    return <strong>{x.Name}</strong>;
  };

  return (
    <>
      <div>
        {showOptions || !info ? (
          <>
            {!info && (
              <button
                type="button"
                className="btn btn-secondary btn-xs"
                disabled={props.readOnly}
                onClick={() => {
                  setShowOptions(true);
                }}>
                <Icon type={IconType.upload} /> Select File
              </button>
            )}
            {info && (
              <div className="flex-between">
                <strong>{info?.fileName ?? info?.path ?? 'Unknown Name'}</strong>
                <span
                  className="click bumper"
                  onClick={() => {
                    if (!props.readOnly) {
                      setShowOptions(false);
                    }
                  }}>
                  <Icon type={IconType.close} />
                </span>
              </div>
            )}
          </>
        ) : (
          <div className="file-picker">{showFile()}</div>
        )}
      </div>
      {showOptions && (
        <Modal setModalOpen={setShowOptions} title={'Existing Files'} size="m">
          <div className="existing-file-container">
            {props.valueOptions?.map((x, i) => {
              let fi = getFileInfo(x.Value);
              return (
                <div key={`efo-${i}`} className="file-picker existing-file-item flex-between">
                  {nameOrFile(x)}{' '}
                  <button
                    type="button"
                    className="btn btn-secondary btn-xs"
                    disabled={props.readOnly}
                    onClick={() => {
                      selectExistingFile(x);
                    }}>
                    <Icon type={IconType.save} />
                  </button>
                </div>
              );
            })}

            <div>
              <h4>Upload File</h4>
              Not finding the file you need, upload one.
              <hr />
              <input id={`${props.htmlId}`} type="file" className="form-control" ref={fileInputRef} onChange={onChangeFile} accept={props.exts} disabled={props.readOnly} />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export type FileType = 'pdf' | 'image' | 'video' | 'text' | 'other';
